import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Button,
  InputAdornment,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import OtpModal from "../Auth/OTPVerification/OtpModal";
import SaveIcon from "@mui/icons-material/Save";
import CheckIcon from "@mui/icons-material/Check";
import { verifyNewUserForEdit } from "../../features/auth/authSlice";
import { notifyError } from "../../utils/notificationMsg";
import { isAdminEmail, userEmail } from "../common/Formik/CustomSchema";

const EditableField = ({
  label,
  defaultValue,
  isMobile,
  userValue,
  fromEditForm,
  isAdmin = false,
  endAdornmentIcon: EndAdornmentIcon,
}) => {
  const dispatch = useDispatch();
  const { user, userData, loading } = useSelector((state) => state.auth);
  const {
    user_companyname,
    user_fullname,
    _id,
    user_type,
    user_mobileno,
    user_email,
    user_address,
    user_contactpersonname,
  } = user ?? {};
  const [isChangeEnabled, setChangeEnabled] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const handleEnableChange = () => {
    setChangeEnabled(true);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      user_email: !isMobile ? userValue ?? "" : "",
      user_type: user_type,
      _id: _id?.$oid,
    },
    validationSchema: Yup.object().shape({
      ...(isMobile
        ? {
            user_mobileno: Yup.string()
              .required("Mobile Number is required")
              .matches(/^[0-9]{10}$/, "Mobile number is invalid"),
          }
        : {
            user_email:isAdmin === true ? isAdminEmail: userEmail,
          }),
    }),
    onSubmit: async (values) => {
      setSubmitting(true);
      try {
        let { payload } = await dispatch(verifyNewUserForEdit(values));
        if (payload.status === 200) {
          handleOpen();
        } else {
          notifyError(payload.data);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const [isSubmitting, setSubmitting] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [timer, setTimer] = useState(30);
  const handleOpen = () => {
    setOtpModalOpen(true);
    setTimer(30);
  };
  const handleCloseOtpModal = () => {
    setOtpModalOpen(false);
  };
  const handleVerifyOTPSuccess = () => {
    setChangeEnabled(false);
    setHasChanged(false);
  };
  const handleUpdateInfo = async () => {
    if (formik.isValid) {
      formik.handleSubmit();
    } else {
      formik.setTouched({
        // user_mobileno: true,
        user_email: true,
      });
    }
  };

  useEffect(() => {
    if (
      (isMobile && formik.values.user_mobileno !== (userValue ?? "")) ||
      (!isMobile && formik.values.user_email !== (userValue ?? ""))
    ) {
      setHasChanged(true);
    } else {
      setHasChanged(false);
    }
  }, [formik.values, isMobile, userValue]);

  return (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontSize: ".7rem",
              textAlign: "end",
              cursor: "pointer",
            }}
            onClick={handleEnableChange}
            color={"primary"}
          >
            Change {label}
          </Typography>
          <TextField
            fullWidth
            required
            label={label}
            variant="outlined"
            name={isMobile ? "user_mobileno" : "user_email"}
            sx={{
              mt: 1,
              height: "40px",
              "& .MuiInputBase-root": { height: "40px" },
            }}
            disabled={!isChangeEnabled}
            value={
              isMobile ? formik.values.user_mobileno : formik.values.user_email
            }
            onChange={formik.handleChange}
            error={
              (isMobile &&
                formik.touched.user_mobileno &&
                Boolean(formik.errors.user_mobileno)) ||
              (!isMobile &&
                formik.touched.user_email &&
                Boolean(formik.errors.user_email))
            }
            helperText={
              (isMobile && formik.touched.mobile && formik.errors.mobile) ||
              (!isMobile &&
                formik.touched.user_email &&
                formik.errors.user_email)
            }
            margin="normal"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {EndAdornmentIcon && (
                    <EndAdornmentIcon style={{ fontSize: "18px" }} />
                  )}
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {hasChanged ? (
                    <IconButton
                      edge="end"
                      color="primary"
                      onClick={handleUpdateInfo}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        <Tooltip title="Save">
                          <SaveIcon />
                        </Tooltip>
                      )}
                    </IconButton>
                  ) : (
                    <IconButton edge="end" color="success">
                      <CheckIcon size={24} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
              style: {
                fontWeight: 300,
                height: "40px",
                fontFamily: "Urbane-Light",
              },
              inputProps: {
                style: {
                  padding: "8px 14px",
                  fontSize: "12px",
                },
              },
            }}
          />
        </Box>
      </Box>
      <OtpModal
        open={otpModalOpen}
        handleClose={handleCloseOtpModal}
        timer={timer}
        setTimer={setTimer}
        handleResendOTP={handleUpdateInfo}
        updateUser={true}
        handleVerifyOTPSuccess={handleVerifyOTPSuccess}
        fromEditForm1={fromEditForm}
        userEmail={formik.values.user_email}
      />
    </>
  );
};

export default EditableField;
